import log from 'app/home/general-components/Logging';
import { LocalizationContext } from 'app/localization/Context';
import { useCallback, useMemo, useReducer, useRef, useEffect, useContext } from 'react';

export default function useUserManagement(userId, sendEvent, negotiatedProtocol, profile) {
    const l = useContext(LocalizationContext);

    const dispatchUserEventRef = useRef(undefined);

    const fillJoinedUserFunction = useCallback((user) => {
        user.remove = () => {
            dispatchUserEventRef.current({
                source: "UserChange", inner: {
                    userId: user.user_id,
                    command: "remove"
                }
            });
            sendEvent("remove_user", { "user_id": user.user_id })
        };
        user.changeNotif = (state) => {
            dispatchUserEventRef.current({
                source: "UserChange", inner: {
                    userId: user.user_id,
                    newNotification: {
                        "bind": state
                    },
                    command: "notif"
                }
            });
            sendEvent("change_user", {
                "user_id": user.user_id,
                "notification": {
                    "bind": state
                }
            })
        };
        user.changePermissions = ({ setting, deviceCS, bindS, bindE, scenarioS, scenarioR }) => {
            // console.log({ setting, deviceCS, bindS, bindE, scenarioS, scenarioR });

            let content = {
                "user_id": user.user_id,
                "permissions": {
                    "setting": user["permissions"]["setting"],
                    "device": {
                        "change_state": user["permissions"]["device"]["change_state"]
                    },
                    "bind": {
                        "setting": user["permissions"]["bind"]["setting"],
                        "change_enable": user["permissions"]["bind"]["change_enable"],
                    },
                    "scenario": {
                        "setting": user["permissions"]["scenario"]["setting"],
                        "run": user["permissions"]["scenario"]["run"],
                    }
                }
            }

            if (scenarioR === false) {
                scenarioS = false;
                setting = false;
            }
            if (scenarioS === false) {
                setting = false;
            }
            if (bindE === false) {
                bindS = false;
                setting = false;
            }
            if (bindS === false) {
                setting = false;
            }
            if (deviceCS === false) {
                setting = false;
            }

            if (setting !== undefined) {
                content["permissions"]["setting"] = setting;
            }
            if (deviceCS !== undefined) {
                content["permissions"]["device"]["change_state"] = deviceCS;
            }
            if (bindS !== undefined) {
                content["permissions"]["bind"]["setting"] = bindS;
            }
            if (bindE !== undefined) {
                content["permissions"]["bind"]["change_enable"] = bindE;
            }
            if (scenarioS !== undefined) {
                content["permissions"]["scenario"]["setting"] = scenarioS;
            }
            if (scenarioR !== undefined) {
                content["permissions"]["scenario"]["run"] = scenarioR;
            }

            content = {
                "user_id": content["user_id"],
                "permissions": {
                    "setting": content["permissions"]["setting"],
                    "device": {
                        "change_state":
                            content["permissions"]["setting"] ||
                            content["permissions"]["device"]["change_state"]
                    },
                    "bind": {
                        "setting":
                            content["permissions"]["setting"] ||
                            content["permissions"]["bind"]["setting"],
                        "change_enable":
                            content["permissions"]["setting"] ||
                            content["permissions"]["bind"]["setting"] ||
                            content["permissions"]["bind"]["change_enable"],
                    },
                    "scenario": {
                        "setting":
                            content["permissions"]["setting"] ||
                            content["permissions"]["scenario"]["setting"],
                        "run":
                            content["permissions"]["setting"] ||
                            content["permissions"]["scenario"]["setting"] ||
                            content["permissions"]["scenario"]["run"],
                    }
                }
            }

            // console.log({ setting, deviceCS, bindS, bindE, scenarioS, scenarioR }, content);
            dispatchUserEventRef.current({
                source: "UserChange", inner: {
                    userId: user.user_id,
                    newPermissions: content["permissions"],
                    command: "permissions",
                }
            });
            sendEvent("change_user", content)
        };
        return user;
    }, [sendEvent]);

    const fillPendingUserFunction = useCallback((user) => {
        user.reject = () => {
            dispatchUserEventRef.current({
                source: "PendingUserChange", inner: {
                    userId: user.user_id,
                    command: "reject"
                }
            });
            sendEvent("reject_user", { "user_id": user.user_id })
        };
        user.accept = () => {
            dispatchUserEventRef.current({
                source: "PendingUserChange", inner: {
                    userId: user.user_id,
                    command: "accept"
                }
            });
            sendEvent("accept_user", { "user_id": user.user_id })
        };
        return user;
    }, [sendEvent]);

    const userEventReducer = useCallback((currentAllUsers, event) => {
        switch (event.source) {
            case "UserChange":
                let newJoinedUsers = [];
                for (let i = 0; i < currentAllUsers.joinedUsers.length; i++) {
                    const user = currentAllUsers.joinedUsers[i];
                    if (user.user_id !== event.inner.userId) {
                        newJoinedUsers.push(user);
                    } else {
                        const finalUser = { ...user };
                        switch (event.inner.command) {
                            case "remove":
                                break;
                            case "notif":
                                finalUser["notification"] = event.inner.newNotification;
                                fillJoinedUserFunction(finalUser);
                                newJoinedUsers.push(finalUser);
                                break;
                            case "permissions":
                                finalUser["permissions"] = event.inner.newPermissions;
                                fillJoinedUserFunction(finalUser);
                                newJoinedUsers.push(finalUser);
                                break;
                            default:
                                break;
                        }
                    }
                }
                return {
                    pendingUsers: currentAllUsers.pendingUsers,
                    joinedUsers: newJoinedUsers,
                }
            case "PendingUserChange":
                return {
                    pendingUsers: currentAllUsers.pendingUsers.map((user) => {
                        if (user.user_id !== event.inner.userId)
                            return user;

                        const finalUser = { ...user };
                        finalUser["loading"] = true;
                        fillPendingUserFunction(finalUser);
                        return finalUser;
                    }),
                    joinedUsers: currentAllUsers.joinedUsers,
                }
            case "Gateway":
                let message = event.inner;
                switch (message.ev) {
                    case 'user_list':
                        let _pending_users = message.msg.pending_users;
                        if (_pending_users === undefined) { // for solo
                            _pending_users = [];
                        }
                        return {
                            joinedUsers: message.msg.users.map((u) => { return fillJoinedUserFunction(u) }),
                            pendingUsers: _pending_users.map((u) => { return fillPendingUserFunction(u) })
                        }
                    case 'change_user':
                        return {
                            pendingUsers: currentAllUsers.pendingUsers,
                            joinedUsers: currentAllUsers.joinedUsers.map((user) => {
                                if (user.user_id !== message.msg.user_id)
                                    return user;

                                const finalUser = { ...user };
                                if (message.msg["permissions"] !== undefined) {
                                    finalUser["permissions"] = message.msg["permissions"];
                                }
                                if (message.msg["notification"] !== undefined) {
                                    finalUser["notification"] = message.msg["notification"];
                                }
                                if (message.msg["name"] !== undefined) {
                                    finalUser["name"] = message.msg["name"];
                                }
                                if (message.msg["avatar"] !== undefined) {
                                    finalUser["avatar"] = message.msg["avatar"];
                                }
                                if (message.msg["language"] !== undefined) {
                                    finalUser["language"] = message.msg["language"];
                                }
                                fillJoinedUserFunction(finalUser);
                                return finalUser;
                            })
                        }
                    default:
                        log('unsupported event in useUserManagement:Gateway,', message.ev);
                        break;
                }
                break
            default:
                log('unsupported event in useUserManagement,', event);
                break;
        }
    }, [fillJoinedUserFunction, fillPendingUserFunction]);

    const [allUsers, dispatchUserEvent] = useReducer(userEventReducer,
        { joinedUsers: [], pendingUsers: [] });

    const onUserRelatedEvent = useCallback((message) => {
        dispatchUserEvent({ source: "Gateway", inner: message });
    }, []);

    useEffect(() => {
        dispatchUserEventRef.current = dispatchUserEvent;
    }, [dispatchUserEvent]);

    const currentUser = useMemo(() => {
        for (let i = 0; i < allUsers.joinedUsers.length; i++) {
            const user = allUsers.joinedUsers[i];
            if (user.user_id === userId) {
                return user;
            }
        }

        const supportUserManagement = negotiatedProtocol.hasUserManagement === true;
        return {
            user_id: userId,
            name: undefined,
            avatar: undefined,
            language: undefined,
            notification: {
                bind: !supportUserManagement,
            },
            permissions: {
                setting: !supportUserManagement,
                device: {
                    change_state: !supportUserManagement,
                },
                bind: {
                    setting: !supportUserManagement,
                    change_enable: !supportUserManagement,
                },
                scenario: {
                    setting: !supportUserManagement,
                    run: !supportUserManagement,
                },
            },
        };
    }, [allUsers, userId, negotiatedProtocol]);

    // const updateProfile = profile.updateProfile;
    // const setLanguage = l.setLanguage;
    // useEffect(() => {
    //     if ((currentUser.name !== undefined) && (currentUser.avatar !== undefined)) {
    //         updateProfile(currentUser.name, currentUser.avatar);
    //     }
    //     if (currentUser.language !== undefined) {
    //         setLanguage(currentUser.language);
    //     }
    // }, [currentUser.language, currentUser.name, currentUser.avatar, updateProfile, setLanguage]);

    const updateProfileName = profile.updateProfileName;
    const updateProfileAvatar = profile.updateProfileAvatar;
    useEffect(() => {
        if  ((currentUser.name !== undefined) && (currentUser.name !== null) && (currentUser.name !== "")) {
            updateProfileName(currentUser.name);
        }
        if ((currentUser.avatar !== undefined) && (currentUser.avatar !== null) && (currentUser.avatar !== 0) && (currentUser.avatar !== "0")) {
            updateProfileAvatar(currentUser.avatar);
        }
        if ((currentUser.language !== undefined) && (currentUser.language !== null) && (currentUser.language !== "")) {
            l.setFullLanguage(currentUser.language);
        }
    }, [currentUser.language, currentUser.name, currentUser.avatar, l, 
        updateProfileName, updateProfileAvatar]);

    // only for solo
    const addUser = useCallback((phoneNumber) => {
        sendEvent("add_user", { "phone_number": phoneNumber })
    }, [sendEvent]);


    return [allUsers, currentUser, onUserRelatedEvent, addUser];
}

